import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Button,
  Typography,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  InputAdornment,
  capitalize,
} from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { appService } from '../../App/app.service';
import { connect } from 'react-redux';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ProvisionUserForm from '../../components/ProvisionUserForm';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
  },
  searchField: {
    marginBottom: theme.spacing(2),
    backgroundColor: "#fff",
    '& .MuiOutlinedInput-root': {
      borderRadius: "8px",
    },
  },
  dataGridContainer: {
    height: '65vh',
    width: '100%',
    '& .MuiDataGrid-root': {
      border: 'none',
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#4472c4',
      color: '#fff',
      fontSize: '1rem',
      fontWeight: 'bold',
    },
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: '#4472c4',
      color: '#fff',
      fontWeight: 'bold',
      '&:focus': {
        outline: 'none',
      },
      '&:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold',
      color: '#fff',
    },
    '& .MuiDataGrid-iconSeparator': {
      color: '#fff',
    },
    '& .MuiDataGrid-sortIcon': {
      color: '#fff',
    },
    '& .MuiDataGrid-menuIcon': {
      color: '#fff',
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#e3ecfa',
      },
    },
    '& .MuiDataGrid-row.row-disabled': {
      backgroundColor: '#f2f2f2',
      color: '#616161',
      cursor: 'not-allowed',
      opacity: 0.85,
      filter: 'grayscale(0.5)',
      '&:hover': {
        backgroundColor: '#f2f2f2',
      },
    },
    '& .MuiDataGrid-cell': {
      fontSize: '0.95rem',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
  },
  statusChip: {
    padding: '3px 14px',
    borderRadius: '14px',
    fontSize: 'inherit',
    fontWeight: 600,
    display: 'inline-block',
    lineHeight: 1.2,
  },
}));

const chipMap = {
  'Inactive': {
    backgroundColor: '#fda29b',
    color: 'red'
  },
  'Active': {
    backgroundColor: '#c8e6c9',
    color: '#1b5e20'
  },
  'Not Provisioned': {
    backgroundColor: '#d6d6d6',
    color: '#3d3d3d'
  }
};

const ProvisionUserManagement = (props) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [team, setTeam] = useState("");
  const [roleType, setRoleType] = useState('');
  const [disableTeam, setDisableTeam] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showNonCrmUserForm, setShowNonCrmUserForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingUserValues, setPendingUserValues] = useState(null);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    props.getCRMUsersData();
  }, []);

  useEffect(() => {
    if (
      props.teamRoleInfo !== "" &&
      props.teamRoleInfo.teamAndRoleType !== null &&
      props.teamRoleInfo.teamAndRoleType !== undefined
    ) {
      setTeam(props.teamRoleInfo.teamAndRoleType.team);
    }
  }, [props.teamRoleInfo]);

  useEffect(() => {
    if (activeStep === 1 && selectedRow !== null) {
      props.getTeamRoleInfo(selectedRow.Email);
    } else if (showNonCrmUserForm) {
      props.getTeamRoleInfo(null);
    }
  }, [showNonCrmUserForm, activeStep]);

  useEffect(() => {
    if (props.crmUserManagement && props.crmUserManagement !== "") {
      const filtered = props.crmUserManagement
        .filter(user =>
          user.Full_Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.Email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.UserID?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
        .map((user, index) => ({
          ...user,
          id: user.UserID || `user-${index}-${user.Email}` // Add unique id property
        }));
      setFilteredUsers(filtered);
    }
  }, [props.crmUserManagement, searchTerm]);

  useEffect(() => {
    if (props.isUserInvited) {
      props.setView("activeUserManagement");
    }
  }, [props.isUserInvited]);

  const handleChangeTeam = (filteredValues) => {
    setTeam(filteredValues);
  };

  const handleChangeRoleType = (ev, val) => {
    const _role = ev.target.value;
    setRoleType(_role);
    if (_role === "admin" || _role === "Admin") {
      setTeam("All");
      setDisableTeam(true);
    } else {
      setDisableTeam(false);
      setTeam("");
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleProvisionClick = (row) => {
    setSelectedRow(row);
    setActiveStep(1);
  };

  const getInitialValues = (row) => {
    if (!row) {
      return {
        firstName: '',
        lastName: '',
        email: '',
        roleType: roleType,
        team: team,
        uiRoleId: '',
      };
    }
    const names = row.Full_Name ? row.Full_Name.split(' ') : ['', ''];
    return {
      firstName: names[0] || '',
      lastName: names.slice(1).join(' ') || '',
      email: row.Email || '',
      roleType: roleType,
      team: team,
      uiRoleId: '',
    };
  };

  // DataGrid columns
  const columns = [
    {
      field: 'Full_Name',
      headerName: 'Name',
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'Email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 250,
    },
    {
      field: 'Status',
      headerName: 'Status',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const status = params.value;
        const style = chipMap[status] || {};
        return (
          <span
            className={classes.statusChip}
            style={{
              backgroundColor: style.backgroundColor,
              color: style.color
            }}
          >
            {status}
          </span>
        );
      },
    },
  ];

  const handleRowClick = (params) => {
    const row = params.row;
    const isProvisioned = row.IsProvisioned === 1;
    const isSelectable = !isProvisioned;

    if (isSelectable) {
      handleProvisionClick(row);
    }
  };

  const getRowClassName = (params) => {
    const isProvisioned = params.row.IsProvisioned === 1;
    return isProvisioned ? 'row-disabled' : '';
  };

  return (
    <Grid container>
      <Card style={{ width: '100%', boxSizing: 'border-box', padding: '1.5rem 2rem' }}>
        <Grid item xs={12}></Grid>
        {activeStep === 0 && !showNonCrmUserForm && (
          <Grid item xs={12}>
            <Paper className={classes.card}>
              <Grid container justifyContent="space-between" alignItems="center" style={{ marginBottom: 16 }}>
                <Grid item container xs={8} justifyContent='flex-start'>
                  <Grid item xs={12}>
                    <Typography variant='h5' style={{ fontWeight: 600 }}>
                      Select from Active CRM Users (Recommended)
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body1'>
                      Adding users from the CRM ensures data consistency
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={4} container justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setShowNonCrmUserForm(true)}
                  >
                    Provision Non-CRM User
                  </Button>
                </Grid>
              </Grid>
              <Grid item container justifyContent='space-between'>
                <Grid item xs={4} container>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search users by name, email, or ID..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className={classes.searchField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid xs={6} container item style={{ padding: '1rem' }} justifyContent='flex-end'>
                  <Button onClick={() => props.setView("activeUserManagement")} variant='outlined'>
                    Cancel
                  </Button>
                </Grid>
              </Grid>
              <div className={classes.dataGridContainer}>
                <DataGrid
                  rows={filteredUsers}
                  columns={columns}
                  pageSize={pageSize}
                  onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  pagination
                  disableSelectionOnClick
                  onRowClick={handleRowClick}
                  getRowClassName={getRowClassName}
                  density="standard"
                  rowHeight={60}
                  disableColumnMenu={true}
                />
              </div>
            </Paper>
          </Grid>
        )}
        {showNonCrmUserForm && (
          <Grid item xs={12} style={{ marginTop: 32, position: 'relative' }}>
            <ProvisionUserForm
              setRoleType={setRoleType}
              setView={props.setView}
              style={{ display: showNonCrmUserForm ? 'block' : 'none' }}
              title="New Non-CRM User"
              initialValues={getInitialValues(null)}
              teamRoleInfo={props.teamRoleInfo}
              disableTeam={disableTeam}
              onBack={() => setShowNonCrmUserForm(false)}
              backIcon={<ArrowBackIcon style={{ fontSize: 28, color: '#4472c4' }} />}
              onSubmit={(values) => {
                setConfirmDialogOpen(true);
                const val = {
                  company: props.user.SG_Company_ID,
                  email: values.email,
                  firstname: values.firstName,
                  lastname: values.lastName,
                  isNonCRMUser: true,
                  team: values.team,
                  roleType: values.roleType,
                  uiRoleId: `${values.uiRoleId}`
                };
                setPendingUserValues(val);
              }}
              team={team}
              isEditable={true}
              handleChangeTeam={handleChangeTeam}
              handleChangeRoleType={handleChangeRoleType}
              loading={loading}
            />
          </Grid>
        )}
        {activeStep === 1 && (
          <Grid item xs={12} style={{ marginTop: 32, position: 'relative' }}>
            <ProvisionUserForm
              setView={props.setView}
              title="New User"
              setRoleType={setRoleType}
              initialValues={getInitialValues(selectedRow)}
              teamRoleInfo={props.teamRoleInfo}
              disableTeam={disableTeam}
              onBack={() => setActiveStep(0)}
              backIcon={<ArrowBackIcon style={{ fontSize: 28, color: '#4472c4' }} />}
              onSubmit={(values) => {
                const val = {
                  company: props.user.SG_Company_ID,
                  email: values.email,
                  firstname: values.firstName,
                  lastname: values.lastName,
                  isNonCRMUser: false,
                  team: values.team,
                  roleType: values.roleType,
                  uiRoleId: `${values.uiRoleId}`
                };
                setPendingUserValues(val);
                setConfirmDialogOpen(true);
              }}
              team={team}
              isEditable={false}
              handleChangeTeam={handleChangeTeam}
              handleChangeRoleType={handleChangeRoleType}
              loading={loading}
            />
          </Grid>
        )}
        <Dialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          aria-labelledby="provision-dialog-title"
          aria-describedby="provision-dialog-description"
        >
          <DialogTitle id="provision-dialog-title">
            Confirm Provision User
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="provision-dialog-description">
              Are you sure you want to provision <strong><span style={{ whiteSpace: 'pre' }}>{pendingUserValues?.firstname}</span> <span style={{ whiteSpace: 'pre' }}>{pendingUserValues?.lastname}</span></strong> (<span style={{ fontWeight: 'bold' }}>{pendingUserValues?.email}</span>) <span>{typeof (pendingUserValues?.roleType) === "string" ?
                ["admin", "c-level"].includes(pendingUserValues?.roleType) ? <> with <span style={{ fontWeight: 600 }}>{capitalize(pendingUserValues?.roleType)} role</span></> : <>as a <span style={{ fontWeight: 600 }}>{capitalize(pendingUserValues?.roleType)}</span></>
                : pendingUserValues?.roleType}</span> {!["admin", "c-level"].includes(pendingUserValues?.roleType) && <>for <span style={{ fontWeight: 600 }}>{pendingUserValues?.team}</span></>}?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ justifyContent: 'center', paddingBottom: '1.5rem' }}>
            <Button
              onClick={() => {
                setConfirmDialogOpen(false);
                setLoading(true);
                const val = {
                  company: props.user.SG_Company_ID,
                  email: pendingUserValues.email,
                  firstname: pendingUserValues.firstname,
                  lastname: pendingUserValues.lastname,
                  isNonCRMUser: pendingUserValues.isNonCRMUser,
                  team: pendingUserValues.team,
                  roleType: pendingUserValues.roleType,
                  uiRoleId: pendingUserValues.uiRoleId
                };
                setLoading(true);
                props.inviteUser(val).finally(() => {
                  setLoading(false);
                });
              }}
              color="primary"
              variant="contained"
              autoFocus
            >
              Yes
            </Button>
            <Button onClick={() => setConfirmDialogOpen(false)} color="primary" variant="contained">
              No
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  const { crmUserManagement, user, teamRoleInfo, isUserInvited, inviteEmail, inviteError } = state.app;

  return {
    crmUserManagement: crmUserManagement,
    user,
    Value_Label: user.Value_Label,
    teamRoleInfo,
    isUserInvited,
    inviteEmail,
    inviteError
  };
};

const mapDispatchToProps = (dispatch) => ({
  getCRMUsersData: (company) => {
    dispatch({ type: "get_crm_users_data_request" });
    appService.getCRMUsersData().then(
      (json) => {
        dispatch({ type: "get_crm_users_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_users_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_crm_users_data_failure", error });
      }
    );
  },
  getTeamRoleInfo: (email) => {
    dispatch({ type: "get_team_role_data_request" });
    appService.getTeamRoleInfo(email).then(
      (json) => {
        dispatch({ type: "get_team_role_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_team_role_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_team_role_data_failure", error });
      }
    );
  },
  inviteUser: (inviteBody) => {
    dispatch({ type: "invite_user_request", email: inviteBody.email });
    return appService.inviteUser(inviteBody).then(
      (json) => {
        dispatch({ type: "invite_user_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "invite_user_failure",
            error: error.message,
          });
        else dispatch({ type: "invite_user_failure", error });
      }
    );
  },
});

const ProvisionUserManagementSection = connect(
  mapStateToProps,
  mapDispatchToProps
)(ProvisionUserManagement);

export default ProvisionUserManagementSection;